import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  User, Mail, Calendar, Shield, Key, AlertTriangle, 
  Check, Loader2, Lock, Smartphone, Eye, EyeOff,
  Building2, Upload, Trash2, Phone, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'business', label: 'Business Profile', icon: Building2 },
  { id: 'password', label: 'Password', icon: Lock },
  { id: 'security', label: 'Security', icon: Shield },
];

export const ProfilePage = () => {
  const { user, token, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');

  // Profile form
  const [fullName, setFullName] = useState('');
  const [saving, setSaving] = useState(false);

  // Business profile
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // 2FA
  const [toggling2FA, setToggling2FA] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  const fetchProfile = useCallback(async () => {
    const authToken = token || localStorage.getItem('token');
    if (!authToken) { setLoading(false); return; }
    try {
      const response = await fetch(`${API_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFullName(data.full_name || '');
        setCompanyName(data.company_name || '');
        setCompanyAddress(data.company_address || '');
        setCompanyEmail(data.company_email || '');
        setCompanyPhone(data.company_phone || '');
        setCompanyLogo(data.company_logo || '');
      }
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (token) fetchProfile();
    else setLoading(false);
  }, [token, fetchProfile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const authToken = token || localStorage.getItem('token');
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ full_name: fullName })
      });
      if (response.ok) {
        toast.success('Profile updated');
        await refreshUser();
        await fetchProfile();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    const authToken = token || localStorage.getItem('token');
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/forms/upload/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyLogo(data.url);
        toast.success('Logo uploaded');
      } else {
        toast.error('Failed to upload logo');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingLogo(false); }
  };

  const handleSaveBusiness = async (e) => {
    e.preventDefault();
    const authToken = token || localStorage.getItem('token');
    setSavingBusiness(true);
    try {
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          company_name: companyName,
          company_address: companyAddress,
          company_email: companyEmail,
          company_phone: companyPhone,
          company_logo: companyLogo,
        })
      });
      if (response.ok) {
        toast.success('Business profile saved');
        await fetchProfile();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save business profile'); }
    finally { setSavingBusiness(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    const authToken = token || localStorage.getItem('token');
    setChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/user/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      if (response.ok) {
        toast.success('Password changed');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch { toast.error('Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const handleToggle2FA = async (nextChecked) => {
    const authToken = token || localStorage.getItem('token');

    // Turning OFF — disable immediately via the existing endpoint
    if (!nextChecked) {
      const confirmed = window.confirm(
        'Disable Email OTP Verification? Your account will be less secure.'
      );
      if (!confirmed) return;
      setToggling2FA(true);
      try {
        const response = await fetch(`${API_URL}/api/user/toggle-2fa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        });
        if (response.ok) {
          toast.success('Email OTP Verification disabled');
          await fetchProfile();
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Failed to disable 2FA');
        }
      } catch { toast.error('Failed to disable 2FA'); }
      finally { setToggling2FA(false); }
      return;
    }

    // Turning ON — request OTP first, open dialog to verify
    setToggling2FA(true);
    try {
      const response = await fetch(`${API_URL}/api/user/2fa/enable-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        toast.success('Verification code sent to your email');
        setOtpCode('');
        setOtpDialogOpen(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send verification code');
      }
    } catch { toast.error('Failed to send verification code'); }
    finally { setToggling2FA(false); }
  };

  const handleVerifyOtpAndEnable2FA = async () => {
    const authToken = token || localStorage.getItem('token');
    const code = otpCode.trim();
    if (code.length < 4) {
      toast.error('Please enter the code from your email');
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${API_URL}/api/user/2fa/enable-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ code }),
      });
      if (response.ok) {
        toast.success('Email OTP Verification is now enabled');
        setOtpDialogOpen(false);
        setOtpCode('');
        await fetchProfile();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Invalid or expired code');
      }
    } catch { toast.error('Failed to verify code'); }
    finally { setVerifyingOtp(false); }
  };

  const handleResendOtp = async () => {
    const authToken = token || localStorage.getItem('token');
    setResendingOtp(true);
    try {
      const response = await fetch(`${API_URL}/api/user/2fa/enable-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        toast.success('A new code has been sent to your email');
        setOtpCode('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to resend code');
      }
    } catch { toast.error('Failed to resend code'); }
    finally { setResendingOtp(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-6">
            {/* Account Overview */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Account Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" /> {profile?.email}
                      {profile?.is_verified && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><Check className="w-3 h-3 mr-1" />Verified</Badge>}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Plan</p>
                    <p className="font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-slate-400" />
                      <Badge className={(profile?.subscription?.plan || profile?.plan) === 'pro' ? 'bg-purple-100 text-purple-700' : (profile?.subscription?.plan || profile?.plan) === 'growth' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                        {(profile?.subscription?.plan || profile?.plan || 'free').toUpperCase()}
                      </Badge>
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Member Since</p>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Sign-in Method</p>
                    <p className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4 text-slate-400" />
                      {profile?.oauth_provider
                        ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{profile.oauth_provider === 'google' ? 'Google' : profile.oauth_provider === 'microsoft' ? 'Microsoft' : profile.oauth_provider}</Badge>
                        : <Badge variant="outline">Email & Password</Badge>}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Edit Name */}
            <Card>
              <CardHeader><CardTitle>Profile Information</CardTitle><CardDescription>Update your personal information</CardDescription></CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your full name" data-testid="profile-fullname-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input value={profile?.email || ''} disabled className="bg-slate-50" />
                    <p className="text-xs text-slate-500">Email cannot be changed</p>
                  </div>
                  <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        );

      case 'business':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" /> Business Profile</CardTitle>
              <CardDescription>This information appears on your documents, signed PDFs, and certificates</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveBusiness} className="space-y-5">
                {/* Logo */}
                <div className="space-y-2">
                  <Label>Company Logo</Label>
                  <div className="flex items-center gap-4">
                    {companyLogo ? (
                      <div className="relative w-20 h-20 rounded-lg border border-slate-200 overflow-hidden bg-white flex items-center justify-center">
                        <img src={companyLogo.startsWith('/api') ? `${API_URL}${companyLogo}` : companyLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                        <button type="button" onClick={() => setCompanyLogo('')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600" data-testid="remove-logo-btn">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                        <Building2 className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <div>
                      <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                        {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {companyLogo ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG, or WebP. Max 5MB.</p>
                    </div>
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleUploadLogo} className="hidden" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Corporation" data-testid="company-name-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyAddress">Business Address</Label>
                  <Input id="companyAddress" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="123 Business Street, City, State - 400001" data-testid="company-address-input" />
                  <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Full address shown in document header</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyEmail">Business Email</Label>
                    <Input id="companyEmail" type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} placeholder="contact@company.com" data-testid="company-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyPhone">Business Phone</Label>
                    <Input id="companyPhone" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="company-phone-input" />
                  </div>
                </div>

                {/* Preview */}
                {companyName && (
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">Document Header Preview</p>
                    <div className="flex items-center justify-between pb-3 border-b border-slate-300">
                      <div className="flex items-center gap-3">
                        {companyLogo && (
                          <img src={companyLogo.startsWith('/api') ? `${API_URL}${companyLogo}` : companyLogo} alt="" className="w-10 h-10 object-contain" />
                        )}
                        <p className="font-bold text-slate-900 text-sm">{companyName}</p>
                      </div>
                      {(companyPhone || companyEmail || companyAddress) && (
                        <div className="pl-4 border-l-2 border-slate-300 text-right">
                          {companyPhone && <p className="text-xs text-slate-600">{companyPhone}</p>}
                          {companyEmail && <p className="text-xs text-slate-600">{companyEmail}</p>}
                          {companyAddress && <p className="text-xs text-slate-500">{companyAddress}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button type="submit" disabled={savingBusiness} data-testid="save-business-btn">
                  {savingBusiness ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Business Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>
        );

      case 'password':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Change Password</CardTitle>
              <CardDescription>
                {profile?.is_oauth_user
                  ? `You signed in using ${profile.oauth_provider === 'google' ? 'Google' : 'Microsoft'}. Password changes must be done through your provider.`
                  : 'Update your password to keep your account secure'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {profile?.is_oauth_user ? (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">OAuth Account</p>
                    <p className="text-sm text-amber-700 mt-1">Visit your {profile.oauth_provider === 'google' ? 'Google' : 'Microsoft'} account settings to change your password.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input id="currentPassword" type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" required data-testid="current-password-input" />
                      <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input id="newPassword" type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" required minLength={8} data-testid="new-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input id="confirmPassword" type={showPasswords ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required minLength={8} data-testid="confirm-password-input" />
                  </div>
                  <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                    {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing...</> : 'Change Password'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5" /> Two-Factor Authentication</CardTitle>
                <CardDescription>Add an extra layer of security</CardDescription>
              </CardHeader>
              <CardContent>
                {profile?.is_oauth_user ? (
                  <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">OAuth Account</p>
                      <p className="text-sm text-blue-700 mt-1">2FA is managed by your {profile.oauth_provider === 'google' ? 'Google' : 'Microsoft'} account.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">Email OTP Verification</p>
                      <p className="text-sm text-slate-500">
                        {profile?.two_factor_enabled ? 'A code will be sent to your email during login' : 'Receive a one-time code via email when you sign in'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {profile?.two_factor_enabled && <Badge className="bg-green-100 text-green-700">Enabled</Badge>}
                      <Switch checked={profile?.two_factor_enabled || false} onCheckedChange={(checked) => handleToggle2FA(checked)} disabled={toggling2FA} data-testid="toggle-2fa-switch" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader><CardTitle className="text-red-600">Danger Zone</CardTitle><CardDescription>Irreversible actions</CardDescription></CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium text-slate-900">Delete Account</p>
                    <p className="text-sm text-slate-500">Permanently delete your account and all data</p>
                  </div>
                  <Button variant="destructive" disabled>Delete Account</Button>
                </div>
                <p className="text-xs text-slate-400 mt-4">Contact support to delete your account.</p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-slate-900" data-testid="profile-page-title">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your account and preferences</p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar tabs */}
          <nav className="md:w-56 shrink-0" data-testid="settings-tabs">
            <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Right content */}
          <div className="flex-1 min-w-0" data-testid="settings-content">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Email OTP verification dialog (Enable 2FA) */}
      <Dialog
        open={otpDialogOpen}
        onOpenChange={(open) => {
          if (!verifyingOtp) {
            setOtpDialogOpen(open);
            if (!open) setOtpCode('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="enable-2fa-otp-dialog">
          <DialogHeader>
            <DialogTitle>Verify your email</DialogTitle>
            <DialogDescription>
              We&apos;ve sent a 6-digit code to <strong>{profile?.email}</strong>. Enter it below to enable Email OTP Verification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="enable-2fa-code">Verification code</Label>
            <Input
              id="enable-2fa-code"
              data-testid="enable-2fa-code-input"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              autoFocus
            />
            <p className="text-xs text-slate-500">
              Didn&apos;t get the code?{' '}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendingOtp || verifyingOtp}
                className="text-indigo-600 hover:underline disabled:opacity-50"
                data-testid="resend-2fa-code-btn"
              >
                {resendingOtp ? 'Resending…' : 'Resend code'}
              </button>
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setOtpDialogOpen(false); setOtpCode(''); }}
              disabled={verifyingOtp}
              data-testid="enable-2fa-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyOtpAndEnable2FA}
              disabled={verifyingOtp || otpCode.trim().length < 4}
              data-testid="enable-2fa-verify-btn"
            >
              {verifyingOtp ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</> : 'Verify & Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ProfilePage;
